import { document, window } from "@packages/global";
import uniqBy from "lodash/uniqBy";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { DrawerContext } from "./drawer.context";

export type DrawerProviderProps = {
  children: ReactNode;
};

export type Drawer = {
  name: string;
  isOpen: boolean;
};

export function DrawerProvider({ children }: DrawerProviderProps) {
  const [drawers, setDrawers] = useState<Drawer[]>([]);

  const close = useCallback(
    (name: string) => {
      setDrawers((drawers) => {
        return uniqBy(
          [
            {
              name,
              isOpen: false,
            },
            ...drawers,
          ],
          ({ name }) => name
        );
      });
    },
    [setDrawers]
  );

  const open = useCallback(
    (name: string) => {
      setDrawers((drawers) => {
        return uniqBy(
          [
            {
              name,
              isOpen: true,
            },
            ...drawers,
          ],
          ({ name }) => name
        );
      });
    },
    [setDrawers]
  );

  const isOpen = useCallback(
    (name: string) => {
      return drawers.find((drawer) => drawer.name === name)?.isOpen ?? false;
    },
    [drawers]
  );

  useEffect(() => {
    const isAnyOpen = drawers.some((drawer) => drawer.isOpen);
    const html = document.documentElement;
    const body = document.body;

    if (!isAnyOpen) {
      html.style.overflow = "";
      html.style.overscrollBehavior = "";
      body.style.overflow = "";
      body.style.overscrollBehavior = "";
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      return;
    }

    const scrollY = window.scrollY ?? window.pageYOffset ?? 0;

    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    return () => {
      html.style.overflow = "";
      html.style.overscrollBehavior = "";
      body.style.overflow = "";
      body.style.overscrollBehavior = "";
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, [drawers]);

  return (
    <DrawerContext.Provider
      value={{ drawers, setDrawers, open, close, isOpen }}
    >
      {children}
    </DrawerContext.Provider>
  );
}
